"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import PropertyInquiryContact from "@/components/property-inquiry-contact"
import {
  MapPin,
  Bed,
  Bath,
  Maximize2,
  Car,
  Heart,
  Share2,
  ChevronLeft,
  ChevronRight,
  Phone,
  MessageSquare,
  Building2,
  Home,
  X,
  Check,
  ArrowLeft,
} from "lucide-react"
import { trackPropertyView } from "@/lib/analytics"

// Property interface
interface Property {
  id: string
  title: string
  slug: string
  description: string
  addressFull: string
  city: string
  propertyType: string
  listingType: string
  bedrooms: number | null
  bathrooms: number | null
  floorAreaSqm: number | null
  parkingSlots: number | null
  rentalPriceMonthly: string
  price: string
  priceCurrency: string
  furnishing: string
  projectName: string
  developerName: string
  propertyCode: string | null
  floorNumber: number | null
  images: string[] | string | null
  primaryImageUrl: string | null
  imageCount: number
}

function formatPrice(price: string | number) {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  if (!numPrice) return 'Contact for Price'
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    maximumFractionDigits: 0,
  }).format(numPrice)
}

function getBedroomLabel(bedrooms: number | null) {
  if (bedrooms === null) return 'N/A'
  if (bedrooms === 0) return 'Studio'
  return `${bedrooms} BR`
}

function getImages(property: Property): string[] {
  if (property.images) {
    try {
      const imagesArray = typeof property.images === 'string'
        ? JSON.parse(property.images)
        : property.images
      if (Array.isArray(imagesArray) && imagesArray.length > 0) {
        return imagesArray.filter(img => img && typeof img === 'string')
      }
    } catch (e) {
      console.error('Error parsing images:', e)
    }
  }

  if (property.primaryImageUrl) {
    return [property.primaryImageUrl]
  }

  return ['/images/placeholders/bgc-condo.jpg']
}

// Breadcrumbs Component
function Breadcrumbs({ property }: { property: Property }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-gray-600 mb-6">
      <Link href="/" className="hover:text-[#d60500] transition-colors">
        <Home className="w-4 h-4" />
      </Link>
      <ChevronRight className="w-4 h-4 text-gray-400" />
      <Link href="/properties" className="hover:text-[#d60500] transition-colors">
        Properties
      </Link>
      <ChevronRight className="w-4 h-4 text-gray-400" />
      <Link href={`/properties?listing_type=${encodeURIComponent(property.listingType)}`} className="hover:text-[#d60500] transition-colors">
        {property.listingType}
      </Link>
      <ChevronRight className="w-4 h-4 text-gray-400" />
      <span className="text-gray-900 font-medium truncate max-w-xs">
        {property.projectName || property.city || 'Metro Manila'}
      </span>
    </nav>
  )
}

// Image Gallery Component
function ImageGallery({ images }: { images: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [imageError, setImageError] = useState<Set<number>>(new Set())

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const handleImageError = (index: number) => {
    setImageError(prev => new Set(prev).add(index))
  }

  return (
    <>
      {/* Main Gallery */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        {/* Large Image */}
        <div
          className="col-span-4 md:col-span-3 relative h-96 md:h-[500px] bg-gray-100 rounded-xl overflow-hidden cursor-pointer group"
          onClick={() => setIsFullscreen(true)}
        >
          <Image
            src={imageError.has(currentIndex) ? '/images/placeholders/bgc-condo.jpg' : images[currentIndex]}
            alt="Property"
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => handleImageError(currentIndex)}
            priority
          />
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
              >
                <ChevronLeft className="w-5 h-5 text-gray-900" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all"
              >
                <ChevronRight className="w-5 h-5 text-gray-900" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/70 text-white text-sm rounded-full">
                {currentIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>

        {/* Thumbnail Grid */}
        {images.length > 1 && (
          <div className="hidden md:block col-span-1 space-y-2 h-[500px] overflow-y-auto">
            {images.slice(0, 10).map((img, idx) => (
              <div
                key={idx}
                className={`relative h-24 bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                  currentIndex === idx ? 'border-red-500 ring-2 ring-red-200' : 'border-transparent hover:border-red-300'
                }`}
                onClick={() => setCurrentIndex(idx)}
              >
                <Image
                  src={imageError.has(idx) ? '/images/placeholders/bgc-condo.jpg' : img}
                  alt={`Thumbnail ${idx + 1}`}
                  fill
                  className="object-cover"
                  onError={() => handleImageError(idx)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Gallery Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 z-50 bg-black">
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          <div className="relative w-full h-full flex items-center justify-center">
            <Image
              src={imageError.has(currentIndex) ? '/images/placeholders/bgc-condo.jpg' : images[currentIndex]}
              alt="Property"
              fill
              className="object-contain"
              onError={() => handleImageError(currentIndex)}
            />

            {images.length > 1 && (
              <>
                <button
                  onClick={handlePrevious}
                  className="absolute left-4 p-4 bg-white/20 hover:bg-white/30 rounded-full transition-all"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-4 p-4 bg-white/20 hover:bg-white/30 rounded-full transition-all"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 bg-black/70 text-white text-lg rounded-full">
                  {currentIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

// Main Component
export default function PropertyDetailClient({ slug }: { slug: string }) {
  const [property, setProperty] = useState<Property | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isFavorited, setIsFavorited] = useState(false)

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await fetch(`/api/properties/${slug}`)
        const data = await response.json()

        if (data.success && data.data) {
          setProperty(data.data)
          // Track property view
          trackPropertyView({
            id: data.data.id,
            title: data.data.title,
            property_type: data.data.propertyType,
            listing_type: data.data.listingType,
            price: data.data.price,
            city: data.data.city,
          })
        } else {
          setError(data.message || "Property not found")
        }
      } catch (err) {
        setError("Failed to load property")
        console.error("Error fetching property:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchProperty()
  }, [slug])

  const handleLike = () => {
    if (!property?.id) return
    const likedProperties = JSON.parse(localStorage.getItem('likedProperties') || '[]')
    let updated: string[]

    if (isFavorited) {
      updated = likedProperties.filter((id: string) => id !== property.id)
    } else {
      updated = [...likedProperties, property.id]
    }

    localStorage.setItem('likedProperties', JSON.stringify(updated))
    setIsFavorited(!isFavorited)
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: property?.title,
        url: window.location.href,
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('Link copied to clipboard!')
    }
  }


  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 400px)' }}>
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#d60500] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">Loading property...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error State
  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 400px)' }}>
          <div className="max-w-md mx-auto px-4 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Property Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'This property may have been removed or is no longer available.'}</p>
            <Link href="/properties">
              <Button className="bg-[#d60500] hover:bg-[#b50400]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Properties
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const images = getImages(property)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 mt-20">
        {/* Back Button & Breadcrumbs */}
        <div className="mb-6">
          <Link
            href="/properties"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#d60500] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back to Properties</span>
          </Link>
          <Breadcrumbs property={property} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Column (2/3) */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <ImageGallery images={images} />

            {/* Property Title & Price */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {property.title}
                  </h1>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{property.addressFull || property.city}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleLike}
                    className="p-3 border border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all"
                  >
                    <Heart className={`w-5 h-5 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-3 border border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all"
                  >
                    <Share2 className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-[#d60500]">
                  {formatPrice(property.rentalPriceMonthly || property.price)}
                </span>
                {(property.listingType === 'For Rent' || property.listingType === 'rent' || property.listingType === 'for_rent') && (
                  <span className="text-lg text-gray-500">/month</span>
                )}
              </div>

              {/* Furnishing Badge */}
              {property.furnishing && (
                <div className="mt-4">
                  <Badge className="bg-red-100 text-[#b50400] border-red-200 px-3 py-1">
                    {property.furnishing}
                  </Badge>
                </div>
              )}
            </div>

            {/* Property Details Grid */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Property Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Bedrooms */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Bed className="w-5 h-5 text-[#d60500]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Bedrooms</div>
                    <div className="font-bold text-gray-900">{getBedroomLabel(property.bedrooms)}</div>
                  </div>
                </div>

                {/* Bathrooms */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Bath className="w-5 h-5 text-[#d60500]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Bathrooms</div>
                    <div className="font-bold text-gray-900">{property.bathrooms || 'N/A'}</div>
                  </div>
                </div>

                {/* Floor Area */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Maximize2 className="w-5 h-5 text-[#d60500]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Floor Area</div>
                    <div className="font-bold text-gray-900">{property.floorAreaSqm ? `${property.floorAreaSqm} m²` : 'N/A'}</div>
                  </div>
                </div>

                {/* Parking */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Car className="w-5 h-5 text-[#d60500]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Parking</div>
                    <div className="font-bold text-gray-900">{property.parkingSlots || 0}</div>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* Floor Number */}
                {property.floorNumber && (
                  <div className="p-4 bg-gray-50 rounded-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <span className="text-gray-700">
                      <span className="font-semibold">Floor:</span> {property.floorNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Broker's Note */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-l-4 border-[#d60500] shadow-sm mb-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 rounded-full bg-[#d60500]/10 flex items-center justify-center">
                    <span className="text-lg">💼</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Broker&apos;s Note</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {(() => {
                      // Generate intelligent broker note based on property characteristics
                      const notes = []

                      // Check for corner unit
                      if (property.title.toLowerCase().includes('corner')) {
                        notes.push('Rare corner layout with enhanced natural lighting and ventilation')
                      }

                      // Check for penthouse
                      if (property.propertyType?.toLowerCase() === 'penthouse' || property.title.toLowerCase().includes('penthouse')) {
                        notes.push('Exclusive penthouse offering with premium finishes and unobstructed views')
                      }

                      // Check for large floor area
                      if (property.floorAreaSqm && property.floorAreaSqm > 150) {
                        notes.push('Exceptionally spacious layout rare in this price range')
                      }

                      // Check for high floor
                      if (property.floorNumber && property.floorNumber > 30) {
                        notes.push('High-floor unit with premium city views')
                      }

                      // Check for multiple parking
                      if (property.parkingSlots && property.parkingSlots >= 2) {
                        notes.push(`${property.parkingSlots} parking slots included - valuable in this location`)
                      }

                      // Check listing type for rental insight
                      if (property.listingType === 'For Rent') {
                        notes.push('Tenant-ready with immediate move-in availability')
                      }

                      // Check for furnished
                      if (property.furnishing && property.furnishing.toLowerCase() !== 'unfurnished') {
                        notes.push('Premium furnishing package included')
                      }

                      // Default professional note if no specific features
                      if (notes.length === 0) {
                        return 'This property has been personally inspected and verified by our team. Priced competitively for current market conditions with strong potential for appreciation.'
                      }

                      // Combine notes with professional closing
                      const mainNote = notes.slice(0, 2).join('. ') + '.'
                      return `${mainNote} Excellent value proposition in this building.`
                    })()}
                  </p>
                  <p className="text-sm text-gray-600 mt-3 italic">
                    — Verified by Spire Group real estate specialists
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About This Property</h2>
              <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">
                {property.description}
              </div>
            </div>

            {/* Building Info */}
            {(property.projectName || property.developerName) && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Building Information</h2>
                <div className="space-y-3">
                  {property.projectName && (
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-[#d60500] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm text-gray-600">Building Name</div>
                        <div className="font-semibold text-gray-900">{property.projectName}</div>
                      </div>
                    </div>
                  )}
                  {property.developerName && (
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#d60500] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm text-gray-600">Developer</div>
                        <div className="font-semibold text-gray-900">{property.developerName}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Right Column (1/3) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Contact Card */}
              <PropertyInquiryContact property={{
                id: property.id,
                title: property.title,
                listingType: property.listingType,
                rentalPriceMonthly: property.rentalPriceMonthly,
                price: property.price,
                referenceNumber: property.propertyCode || undefined
              }} />

              {/* Quick Facts */}
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Quick Facts</h3>
                  <div className="space-y-3 text-sm">
                    {property.propertyCode && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Property Code</span>
                        <span className="font-semibold text-gray-900">{property.propertyCode}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Property Type</span>
                      <span className="font-semibold text-gray-900">{property.propertyType || 'Property'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Listing Type</span>
                      <span className="font-semibold text-gray-900">{property.listingType || 'For Sale'}</span>
                    </div>
                    {property.priceCurrency && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Currency</span>
                        <span className="font-semibold text-gray-900">{property.priceCurrency}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Location</span>
                      <span className="font-semibold text-gray-900">{property.city || property.addressFull || 'Metro Manila'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
